import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Search, ChevronLeft, ChevronRight, Star, Locate } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import { getEstablishments } from '../helpers/establishments';
import { getReviews } from '../helpers/reviews';
import { API_BASE_URL } from '../helpers/api';
import 'leaflet/dist/leaflet.css';
import '../css/Map.css';

// Fix for default marker icons in React-Leaflet
import L from 'leaflet';
import { renderHTML } from '../hooks/tools';
import { Link } from 'react-router-dom';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Recenter map component
const RecenterMap = ({ coordinates }) => {
  const map = useMap();
  useEffect(() => {
    if (coordinates) {
      map.setView(coordinates, 13);
    }
  }, [coordinates, map]);
  return null;
};

const Map = () => {
  usePageTitle('Carte');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredEstablishments, setFilteredEstablishments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const [error, setError] = useState('');

  // Center of Guadeloupe
  const defaultCenter = [16.265, -61.551];

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getUserLocation = () => {
    setIsLoadingLocation(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          
          // Sort establishments by distance from user
          const sortedEstablishments = [...establishments].map(est => ({
            ...est,
            distance: calculateDistance(
              latitude, 
              longitude, 
              est.coordinates[0], 
              est.coordinates[1]
            )
          })).sort((a, b) => a.distance - b.distance);
          
          setFilteredEstablishments(sortedEstablishments);
          setSelectedLocation({ coordinates: [latitude, longitude] });
          setIsLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setIsLoadingLocation(false);
          setError("Impossible d'obtenir votre position. Veuillez vérifier vos paramètres de localisation.");
        }
      );
    } else {
      setError("La géolocalisation n'est pas supportée par votre navigateur.");
      setIsLoadingLocation(false);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [establishmentsData, reviewsData] = await Promise.all([
        getEstablishments(),
        getReviews()
      ]);

      // Create a map of establishment stats
      const reviewStats = {};
      reviewsData?.forEach(review => {
        // Skip if review or establishment is null/undefined
        if (!review?.establishment?.documentId) return;
        
        const estId = review.establishment.documentId;
        if (!reviewStats[estId]) {
          reviewStats[estId] = {
            count: 0,
            sum: 0
          };
        }
        reviewStats[estId].count++;
        reviewStats[estId].sum += review.rating || 0;
      });

      // Process establishments with coordinates and stats
      // console.log('Raw establishments data:', establishmentsData);
      
      const processedEstablishments = establishmentsData
        .filter(est => {
          // Log establishments without coordinates
          if (!est.location?.lat || !est.location?.long) {
            // console.log('Establishment without coordinates:', est.name);
            return false;
          }
          return true;
        })
        .map(establishment => {
          const processed = {
            ...establishment,
            coordinates: [parseFloat(establishment.location.lat), parseFloat(establishment.location.long)],
            reviewStats: reviewStats[establishment.documentId] 
              ? {
                  count: reviewStats[establishment.documentId].count,
                  average: parseFloat((reviewStats[establishment.documentId].sum / 
                    reviewStats[establishment.documentId].count).toFixed(1))
                }
              : { count: 0, average: 0 }
          };
          return processed;
        });
        
      // console.log('Processed establishments:', processedEstablishments);

      setEstablishments(processedEstablishments);
      setFilteredEstablishments(processedEstablishments);
    } catch(e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Filter establishments based on search term
  useEffect(() => {
    let filtered = establishments;
    
    if (searchTerm) {
      filtered = establishments.filter(place => 
        place.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        place.category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        place.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        place.tags.some(tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (userLocation) {
      filtered = filtered.map(est => ({
        ...est,
        distance: calculateDistance(
          userLocation[0],
          userLocation[1],
          est.coordinates[0],
          est.coordinates[1]
        )
      })).sort((a, b) => a.distance - b.distance);
    }
    
    setFilteredEstablishments(filtered);
  }, [searchTerm, establishments, userLocation]);

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
  };

  if (isLoading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="map-page">
      {/* Sidebar */}
      <div className={`map-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="search-bar">
            <Search size={20} />
            <input 
              type="text" 
              placeholder="Rechercher sur la carte..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="near-me-button">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show start-50 translate-middle-x mt-3 z-3" role="alert">
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
              </div>
            )}
            <button 
              onClick={getUserLocation}
              disabled={isLoadingLocation}
              className="btn btn-success w-100 mt-2 d-flex align-items-center justify-content-center gap-2"
            >
              <Locate size={16} />
              {isLoadingLocation ? "Localisation..." : "Autour de moi"}
            </button>
          </div>
        </div>

        <div className="locations-list">
          {filteredEstablishments.map((place) => (
            <div 
              key={place.id} 
              className={`location-card ${selectedLocation?.id === place.id ? 'active' : ''}`}
              onClick={() => handleLocationClick(place)}
            >
              <img 
                src={place.images[0]?.url ? API_BASE_URL + place.images[0].url : '/placeholder.jpg'} 
                alt={place.name} 
                className="location-image"
              />
              <div className="location-info">
                <h3>{place.name}</h3>
                <span className="category">{place.category.name}</span>
                <div className="rating">
                  <Star size={14} />
                  <span>{place.reviewStats.average}</span>
                  <small>({place.reviewStats.count} avis)</small>
                </div>
                <div className="address">
                  <MapPin size={14} />
                  {place.city}
                </div>
                <div className="tags">
                  {place.tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="tag">{tag.name}</span>
                  ))}
                  {place.tags.length > 3 && (
                    <span className="tag-more">+{place.tags.length - 3}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toggle button */}
      <button 
        className="sidebar-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <ChevronLeft size={20} color="#ffffff" /> : <ChevronRight size={20} color="#ffffff" />}
      </button>

      {/* Map Container */}
      <div className="map-container">
        <MapContainer
          center={defaultCenter}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {filteredEstablishments.map((place) => (
            <Marker
              key={place.id}
              position={place.coordinates}
              eventHandlers={{
                click: () => handleLocationClick(place),
              }}
            >
              <Popup>
                <Link to={`/spot/${place.documentId}`} className="establishment-card">
                <div className="map-popup">
                  <img 
                    src={place.images[0]?.url ? API_BASE_URL + place.images[0].url : '/placeholder.jpg'} 
                    alt={place.name} 
                    className="popup-image"
                  />
                  <h3>{place.name}</h3>
                  <p>{renderHTML(place.description, true, 50)}</p>
                  <div className="popup-meta">
                    <span className="category">{place.category.name}</span>
                    <span className="rating">
                      <Star size={14} />
                      {place.reviewStats.average} ({place.reviewStats.count} avis)
                    </span>
                    {place.distance !== undefined && (
                      <span className="distance">
                        <MapPin size={14} />
                        {place.distance.toFixed(1)} km
                      </span>
                    )}
                    <span className="address">
                      <MapPin size={14} />
                      {place.city}
                    </span>
                  </div>
                </div>
                </Link>
              </Popup>
            </Marker>
          ))}
          {userLocation && (
            <Marker position={userLocation}>
              <Popup>Votre position</Popup>
            </Marker>
          )}
          {selectedLocation && (
            <RecenterMap coordinates={selectedLocation.coordinates} />
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default Map;